import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Calendar from 'react-calendar';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  color: #4b5563;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AccountButton = styled(Link)`
  padding: 0.5rem 1rem;
  border-radius: 999px;
  border: 1px solid #4a90e2;
  background-color: #fff;
  color: #4a90e2;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background-color: #4a90e2;
    color: #fff;
    border-color: #4a90e2;
  }
`;

const Section = styled.section`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #111827;
`;

const SectionSubtitle = styled.p`
  margin: 0 0 1rem;
  font-size: 0.9rem;
  color: #4b5563;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  flex: 1;
  min-width: 180px;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 1rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #111827;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 0.5rem;
  color: #111827;
`;

const QuickActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const QuickActionButton = styled(Link)`
  padding: 0.6rem 1.1rem;
  border-radius: 999px;
  border: 1px solid #111827;
  background-color: #111827;
  color: #f9fafb;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.05s ease;

  &:hover {
    background-color: #1f2937;
    border-color: #1f2937;
    transform: translateY(-1px);
  }
`;

const TimeRangeSelector = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TimeRangeButton = styled.button`
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${props => (props.$active ? '#4a90e2' : '#ddd')};
  background-color: ${props => (props.$active ? '#4a90e2' : '#fff')};
  color: ${props => (props.$active ? '#fff' : '#333')};
  font-size: 0.85rem;
  cursor: pointer;
`;

const EmptyState = styled.p`
  margin: 0;
  color: #111827;
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%);
  background-size: 400% 100%;
  border-radius: 6px;
  animation: skeleton-loading 1.2s ease-in-out infinite;

  @keyframes skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
`;

const SkeletonHeaderTitle = styled(SkeletonBlock)`
  width: 220px;
  height: 28px;
  margin-bottom: 0.5rem;
`;

const SkeletonHeaderSubtitle = styled(SkeletonBlock)`
  width: 320px;
  height: 18px;
`;

const SkeletonStatCard = styled(SkeletonBlock)`
  flex: 1;
  min-width: 180px;
  height: 88px;
`;

const SkeletonSectionTitle = styled(SkeletonBlock)`
  width: 180px;
  height: 22px;
  margin-bottom: 1rem;
`;

const SkeletonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const AppointmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const AppointmentCard = styled(Link)`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: #f5f7fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
`;

const AppointmentMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const AppointmentTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  color: #111827;
`;

const AppointmentMeta = styled.div`
  font-size: 0.8rem;
  color: #4b5563;
`;

const AppointmentTime = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
  color: #111827;
`;

const SmallDangerButton = styled.button`
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  border: none;
  background: #b91c1c;
  color: #f9fafb;
  font-size: 0.75rem;
  cursor: pointer;

  &:hover {
    background: #dc2626;
  }
`;

const CalendarLayout = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1.1fr;
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  border: none;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 0.75rem;

  .react-calendar__month-view__weekdays__weekday {
    color: #111827;
    font-weight: 500;
  }

  .react-calendar__tile--now {
    background: #e5f0ff;
    border-radius: 6px;
  }

  .react-calendar__tile--active,
  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: #4a90e2;
    color: #ffffff;
    border-radius: 6px;
  }
`;

const Dashboard = () => {
  const { user, loading } = useAuth();
  const [timeRange, setTimeRange] = useState('month'); // day | week | month | ytd
  const [bookingRequests, setBookingRequests] = useState([]);
  const [bookingRequestsLoading, setBookingRequestsLoading] = useState(false);
  const [bookingRequestsError, setBookingRequestsError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [artistAppointmentsByDate, setArtistAppointmentsByDate] = useState({});
  const [artistAppointments, setArtistAppointments] = useState([]);

  const timeRangeLabelMap = {
    day: 'Today',
    week: 'This Week',
    month: 'This Month',
    ytd: 'Year to Date',
  };

  const displayName =
    user?.profile?.studioName?.trim() ||
    user?.profile?.firstName ||
    user?.email ||
    'Artist';

  const formatKey = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const selectedDateKey = formatKey(selectedDate);
  const dayAppointments = artistAppointmentsByDate[selectedDateKey] || [];

  // Earnings stats: filter appointments into the current time range
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 6); // last 7 days including today
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  const rangeStart =
    timeRange === 'day'
      ? startOfToday
      : timeRange === 'week'
      ? startOfWeek
      : timeRange === 'month'
      ? startOfMonth
      : startOfYear;

  const appointmentsInRange = Array.isArray(artistAppointments)
    ? artistAppointments.filter((appt) => {
        if (!appt.startTime) return false;
        const date = new Date(appt.startTime);
        return date >= rangeStart && date <= now;
      })
    : [];

  const completedInRange = appointmentsInRange.filter(
    (appt) => appt.status === 'completed'
  );

  const totalTattooRevenue = completedInRange.reduce(
    (sum, appt) => sum + (appt.estimatedPrice?.amount || 0),
    0
  );

  const paidDepositAppointments = appointmentsInRange.filter(
    (appt) => appt?.deposit && appt.deposit.isPaid && typeof appt.deposit.amount === 'number'
  );

  const totalDepositRevenue = paidDepositAppointments.reduce(
    (sum, appt) => sum + (appt.deposit?.amount || 0),
    0
  );

  const handleDeleteBookingFromDashboard = async (id) => {
    if (!window.confirm('Delete this consultation request? This cannot be undone.')) return;

    try {
      await api.delete(`/bookings/${id}`);
      setBookingRequests(prev => prev.filter(req => req._id !== id));
    } catch (err) {
      console.error('Failed to delete booking request from dashboard', err);
      alert('Could not delete consultation request.');
    }
  };

  useEffect(() => {
    const fetchBookings = async () => {
      if (!user) return;
      setBookingRequestsLoading(true);
      setBookingRequestsError('');
      try {
        const res = await api.get('/bookings');
        setBookingRequests(res.data || []);
      } catch (err) {
        console.error('Failed to load booking requests', err);
        setBookingRequestsError('Could not load recent booking requests.');
      } finally {
        setBookingRequestsLoading(false);
      }
    };

    fetchBookings();
  }, [user]);

  useEffect(() => {
    const fetchArtistAppointments = async () => {
      if (!user || user.role !== 'artist') return;

      try {
        const res = await api.get('/appointments/artist/my');
        const list = res.data?.data || [];

        setArtistAppointments(list);

        const grouped = list.reduce((acc, appt) => {
          const key = formatKey(appt.startTime);
          const start = new Date(appt.startTime);
          const timeString = start.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          });

          const clientName =
            appt.client?.profile?.firstName ||
            appt.client?.email ||
            'Client';

          const entry = {
            id: appt._id,
            time: timeString,
            client: clientName,
            service: appt.service || 'Tattoo session',
            notes: appt.description,
          };

          if (!acc[key]) acc[key] = [];
          acc[key].push(entry);
          return acc;
        }, {});

        // Sort each day's appointments by time
        Object.keys(grouped).forEach((key) => {
          grouped[key].sort((a, b) => a.time.localeCompare(b.time));
        });

        setArtistAppointmentsByDate(grouped);
      } catch (err) {
        console.error('Failed to load artist appointments for calendar', err);
      }
    };

    fetchArtistAppointments();
  }, [user]);

  if (loading) {
    return (
      <PageContainer>
        <Header>
          <div>
            <SkeletonHeaderTitle />
            <SkeletonHeaderSubtitle />
          </div>
          <HeaderActions>
            <SkeletonBlock style={{ width: 140, height: 36, borderRadius: 999 }} />
          </HeaderActions>
        </Header>

        <Section>
          <Header>
            <div>
              <SkeletonSectionTitle />
              <SkeletonHeaderSubtitle style={{ width: 260 }} />
            </div>
            <TimeRangeSelector>
              {[1, 2, 3, 4].map(key => (
                <SkeletonBlock key={key} style={{ width: 72, height: 30, borderRadius: 999 }} />
              ))}
            </TimeRangeSelector>
          </Header>
          <StatsRow>
            <SkeletonStatCard />
            <SkeletonStatCard />
          </StatsRow>
        </Section>

      <Section>
        <SectionTitle>Appointments with paid deposits</SectionTitle>
        {paidDepositAppointments.length === 0 ? (
          <AppointmentList>
            <EmptyState>No appointments have a paid deposit yet.</EmptyState>
          </AppointmentList>
        ) : (
          <AppointmentList>
            {paidDepositAppointments.map((appt) => {
              const start = appt.startTime ? new Date(appt.startTime) : null;
              const when = start
                ? start.toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })
                : 'TBD';

              const clientName =
                appt.client?.profile?.firstName ||
                appt.client?.email ||
                'Client';

              return (
                <AppointmentCard
                  key={appt._id}
                  to="#"
                  style={{ textDecoration: 'none', cursor: 'default' }}
                >
                  <AppointmentMain>
                    <AppointmentTitle>{appt.service || 'Tattoo session'}</AppointmentTitle>
                    <AppointmentMeta>
                      {clientName} • Deposit paid ${appt.deposit?.amount?.toFixed(2) || '0.00'}
                    </AppointmentMeta>
                  </AppointmentMain>
                  <AppointmentTime>{when}</AppointmentTime>
                </AppointmentCard>
              );
            })}
          </AppointmentList>
        )}
      </Section>

        <Section>
          <SkeletonSectionTitle />
          <SkeletonRow>
            {[1, 2].map(key => (
              <SkeletonBlock key={key} style={{ width: 190, height: 36, borderRadius: 999 }} />
            ))}
          </SkeletonRow>
        </Section>

        <Section>
          <SkeletonSectionTitle />
          <SkeletonBlock style={{ width: '100%', height: 40 }} />
        </Section>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <div>
          <Title>Studio Dashboard</Title>
          <Subtitle>Welcome back, {displayName}. Here's how your tattoo business is doing.</Subtitle>
        </div>
        <HeaderActions>
          <AccountButton to="/account">Account Settings</AccountButton>
        </HeaderActions>
      </Header>

      <Section>
        <SectionTitle>Your calendar</SectionTitle>
        <SectionSubtitle>
          See your schedule at a glance. This view will eventually show confirmed tattoo appointments.
        </SectionSubtitle>
        <CalendarLayout>
          <StyledCalendar
            value={selectedDate}
            onChange={setSelectedDate}
          />
          <div>
            <SectionTitle style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
              Appointments on{' '}
              {selectedDate.toLocaleDateString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
            </SectionTitle>
            <AppointmentList>
              {dayAppointments.length === 0 ? (
                <EmptyState>No appointments yet for this day.</EmptyState>
              ) : (
                dayAppointments.map((appt, index) => (
                  <AppointmentCard key={index} to="#" style={{ textDecoration: 'none', cursor: 'default' }}>
                    <AppointmentMain>
                      <AppointmentTitle>{appt.service}</AppointmentTitle>
                      <AppointmentMeta>
                        {appt.client} • {appt.notes || 'Tattoo session'}
                      </AppointmentMeta>
                    </AppointmentMain>
                    <AppointmentTime>{appt.time}</AppointmentTime>
                  </AppointmentCard>
                ))
              )}
            </AppointmentList>
          </div>
        </CalendarLayout>
      </Section>

      <Section>
        <SectionTitle>Tattoo Earnings</SectionTitle>
        <Subtitle>How much you've earned from tattoos {timeRangeLabelMap[timeRange].toLowerCase()}</Subtitle>
        <div>
          <TimeRangeSelector>
            {['day', 'week', 'month', 'ytd'].map(range => (
              <TimeRangeButton
                key={range}
                type="button"
                $active={timeRange === range}
                onClick={() => setTimeRange(range)}
              >
                {timeRangeLabelMap[range]}
              </TimeRangeButton>
            ))}
          </TimeRangeSelector>
        </div>
        <StatsRow>
          <StatCard>
            <StatLabel>Total tattoo revenue</StatLabel>
            <StatValue>
              ${totalTattooRevenue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Completed tattoo sessions</StatLabel>
            <StatValue>{completedInRange.length}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Deposit revenue</StatLabel>
            <StatValue>
              ${totalDepositRevenue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </StatValue>
          </StatCard>
        </StatsRow>
      </Section>

      <Section>
        <SectionTitle>Quick actions</SectionTitle>
        <QuickActionsRow>
          <QuickActionButton to="/booking">New booking request</QuickActionButton>
          <QuickActionButton to="/ai-preview">Preview AI replies</QuickActionButton>
        </QuickActionsRow>
      </Section>

      <Section>
        <SectionTitle>Recent consultations booked</SectionTitle>
        {bookingRequestsLoading ? (
          <AppointmentList>
            <EmptyState>Loading booking requests…</EmptyState>
          </AppointmentList>
        ) : bookingRequestsError ? (
          <EmptyState>{bookingRequestsError}</EmptyState>
        ) : bookingRequests.length === 0 ? (
          <EmptyState>No booking requests yet. When clients send requests from your booking form, they'll appear here.</EmptyState>
        ) : (
          <AppointmentList>
            {bookingRequests.map((req) => (
              <AppointmentCard key={req._id} to={`/bookings/${req._id}`} style={{ textDecoration: 'none' }}>
                <AppointmentMain>
                  <AppointmentTitle>{req.name}</AppointmentTitle>
                  <AppointmentMeta>
                    {req.placement || 'Placement not specified'} • {req.styleIdea ? 'Has idea/details' : 'No details yet'}
                  </AppointmentMeta>
                </AppointmentMain>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <AppointmentTime>
                    {new Date(req.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </AppointmentTime>
                  <SmallDangerButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteBookingFromDashboard(req._id);
                    }}
                  >
                    Delete
                  </SmallDangerButton>
                </div>
              </AppointmentCard>
            ))}
          </AppointmentList>
        )}
      </Section>

      <Section>
        <SectionTitle>Today's tattoo appointments</SectionTitle>
        <AppointmentList>
          <EmptyState>No appointments yet for today.</EmptyState>
        </AppointmentList>
      </Section>

      {(user?.profile?.instagramHandle ||
        user?.profile?.facebookHandle ||
        user?.profile?.tiktokHandle ||
        user?.profile?.website ||
        (Array.isArray(user?.profile?.preferredStyles) && user.profile.preferredStyles.length) ||
        user?.profile?.hourlyRate ||
        user?.profile?.shopMinimum ||
        user?.profile?.depositAmount ||
        user?.profile?.halfDayRate ||
        user?.profile?.fullDayRate ||
        user?.profile?.workingHours) && (
        <Section>
          <SectionTitle>Your studio info</SectionTitle>
          <SectionSubtitle>
            This is how your basic info and rates are set up right now. You can update any of this in Account
            Settings.
          </SectionSubtitle>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.35rem' }}>
            {user?.profile?.instagramHandle && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Instagram:</span> {user.profile.instagramHandle}
              </li>
            )}
            {user?.profile?.facebookHandle && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Facebook:</span> {user.profile.facebookHandle}
              </li>
            )}
            {user?.profile?.tiktokHandle && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>TikTok:</span> {user.profile.tiktokHandle}
              </li>
            )}
            {user?.profile?.website && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Website:</span> {user.profile.website}
              </li>
            )}
            {Array.isArray(user?.profile?.preferredStyles) && user.profile.preferredStyles.length > 0 && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Preferred styles:</span>{' '}
                {user.profile.preferredStyles.join(', ')}
              </li>
            )}
            {user?.profile?.hourlyRate && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Hourly rate:</span> {user.profile.hourlyRate}
              </li>
            )}
            {user?.profile?.shopMinimum && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Shop minimum:</span> {user.profile.shopMinimum}
              </li>
            )}
            {user?.profile?.depositAmount && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Deposit to book:</span> {user.profile.depositAmount}
              </li>
            )}
            {user?.profile?.halfDayRate && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Half-day session:</span> {user.profile.halfDayRate}
              </li>
            )}
            {user?.profile?.fullDayRate && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Full-day session:</span> {user.profile.fullDayRate}
              </li>
            )}
            {user?.profile?.workingHours && (
              <li style={{ fontSize: '0.9rem', color: '#111827' }}>
                <span style={{ color: '#6b7280' }}>Working hours:</span> {user.profile.workingHours}
              </li>
            )}
          </ul>
        </Section>
      )}

      <Section>
        <SectionTitle>Upcoming tattoo appointments</SectionTitle>
        <EmptyState>
          You don't have any upcoming tattoo appointments yet. Once clients start booking, you'll see them here.
        </EmptyState>
      </Section>
    </PageContainer>
  );
};

export default Dashboard;
